import PropTypes from 'prop-types';

const num = PropTypes.number;
const str = PropTypes.string;
const { bool } = PropTypes;
const fn = PropTypes.func;
const numOrStr = PropTypes.oneOfType([num, str]);

const labelValueShape = PropTypes.shape({
    value: str,
    label: str,
    id: num,
});

const latLngShape = PropTypes.shape({ lat: num, lng: num });

export const routerLocationShape = PropTypes.shape({
    pathname: str,
    search: str,
    hash: str,
    // react-router lets callers pass arbitrary application data here; can't be typed
    // eslint-disable-next-line react/forbid-prop-types
    state: PropTypes.any,
    key: str,
});

export const routerHistoryShape = PropTypes.shape({
    push: fn,
    replace: fn,
    goBack: fn,
    length: num,
    location: routerLocationShape,
});

export const routerMatchShape = PropTypes.shape({
    params: PropTypes.shape({
        eventId: numOrStr,
        clubId: numOrStr,
        playerId: numOrStr,
        drawId: numOrStr,
    }),
    path: str,
    url: str,
    isExact: bool,
});

export const geoLocationShape = PropTypes.shape({
    display: str,
    googleFormattedName: str,
    placeName: str,
    streetAddress: str,
    stateName: str,
    cityStateZip: str,
    latLng: latLngShape,
    locationName: str,
    locationCoordinates: latLngShape,
    facilities: PropTypes.arrayOf(PropTypes.shape({
        id: num,
        name: str,
    })),
});

export const timeZoneShape = PropTypes.shape({
    id: num,
    value: str,
    label: str,
    name: str,
    offset: numOrStr,
});

const clubMembershipShape = PropTypes.shape({
    clubId: num,
    roleId: num,
    isOrganizer: bool,
});

export const userShape = PropTypes.shape({
    id: num,
    firstName: str,
    lastName: str,
    email: str,
    secondaryEmail: str,
    phone: str,
    gender: str,
    birthdate: str,
    month: num,
    location: PropTypes.shape({
        display: str,
        latLng: latLngShape,
    }),
    isPowered: bool,
    claimedPlayer: PropTypes.shape({ playerId: num }),
    subscriptionType: numOrStr,
    subscriptionTierType: numOrStr,
    subscriptionGateway: str,
    hasPastDueSubscription: bool,
    memberPromoCreditBalance: PropTypes.shape({ balance: num }),
    onboardingRequired: bool,
    onboardingCompleted: bool,
    onboardingStep: numOrStr,
    requiredOnboardingFlowType: numOrStr,
    clubMemberships: PropTypes.arrayOf(clubMembershipShape),
    isProClubHeadOrganizer: bool,
    isAustralian: bool,
});

const preferencesShape = PropTypes.shape({
    emailEnabled: bool,
    pushEnabled: bool,
});

export const authShape = PropTypes.shape({
    user: userShape,
    feedPreferences: preferencesShape,
    settings: PropTypes.shape({
        emailFrequency: str,
        notificationsEnabled: bool,
    }),
    australiaSettings: PropTypes.shape({
        memberClubId: num,
        memberId: num,
    }),
    paidHitClub: PropTypes.oneOfType([
        bool,
        PropTypes.shape({ id: num, name: str }),
    ]),
    paidHitClubPaymentAccountsLoaded: bool,
    saving: bool,
    // generic error envelope from auth flows; shape varies by failure mode
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.any,
    redirectUri: str,
});

const utrRangeShape = PropTypes.shape({
    pkbRatingDisplay: str,
    pkbRating: num,
    singlesUtrDisplay: str,
});

const profileImageShape = PropTypes.shape({
    url: str,
    isPrimary: bool,
});

const historicRatingShape = PropTypes.shape({
    date: str,
    rating: num,
});

const divisionRefShape = PropTypes.shape({
    id: num,
    name: str,
    divisionId: num,
});

export const playerShape = PropTypes.shape({
    id: num,
    memberId: num,
    playerId: num,
    firstName: str,
    lastName: str,
    displayName: str,
    email: str,
    phone: str,
    gender: str,
    age: num,
    nationality: str,
    location: PropTypes.oneOfType([str, PropTypes.shape({ display: str })]),
    singlesUtr: numOrStr,
    doublesUtr: num,
    myUtrSingles: num,
    singlesPkbRatingDisplay: str,
    doublesPkbRatingDisplay: str,
    ratingStatusSingles: numOrStr,
    utrRange: PropTypes.oneOfType([str, utrRangeShape]),
    highSchool: str,
    homeCourt: str,
    divisions: PropTypes.arrayOf(divisionRefShape),
    registeredDivisions: PropTypes.arrayOf(divisionRefShape),
    draws: PropTypes.arrayOf(PropTypes.shape({ id: num, name: str })),
    source: PropTypes.shape({ id: num, isPro: bool }),
    members: PropTypes.arrayOf(PropTypes.shape({ id: num, firstName: str, lastName: str })),
    playerProfileImages: PropTypes.arrayOf(profileImageShape),
    clubProfileImages: PropTypes.arrayOf(profileImageShape),
    historicRatings: PropTypes.arrayOf(historicRatingShape),
    primarySortValue: num,
    secondarySortValue: num,
    acceptanceListWildcardStateId: num,
    isSelected: bool,
    rank: num,
    key: str,
});

export const playersDictByMemberIdShape = PropTypes.objectOf(playerShape);

const eventDivisionShape = PropTypes.shape({
    id: num,
    name: str,
    genderId: num,
    minUtr: num,
    maxUtr: num,
    entryFee: num,
});

const eventDrawRefShape = PropTypes.shape({
    id: num,
    name: str,
    drawId: num,
    eliminationTypeId: num,
});

const eventLocationShape = PropTypes.shape({
    id: num,
    name: str,
    address: str,
    city: str,
});

const eventTourShape = PropTypes.shape({
    id: num,
    name: str,
});

const eventCurrentMemberShape = PropTypes.shape({
    id: num,
    isAdmin: bool,
    isRegistered: bool,
});

export const eventShape = PropTypes.shape({
    id: num,
    name: str,
    published: bool,
    eventDivisions: PropTypes.arrayOf(eventDivisionShape),
    draws: PropTypes.arrayOf(eventDrawRefShape),
    eventSchedule: PropTypes.shape({
        eventStartUtc: str,
        timeZone: timeZoneShape,
        registrationEndString: str,
    }),
    eventStateId: num,
    sportTypeId: num,
    utrTypeId: num,
    utrType: labelValueShape,
    eventState: labelValueShape,
    club: PropTypes.shape({ id: num, name: str }),
    currency: PropTypes.shape({ symbol: str, code: str }),
    details: str,
    allowOffPlatformPayments: bool,
    currentMember: eventCurrentMemberShape,
    startDate: str,
    momentEndDate: str,
    registered: bool,
    registeredPlayers: PropTypes.arrayOf(PropTypes.shape({ id: num, memberId: num })),
    eventLocations: PropTypes.arrayOf(eventLocationShape),
    email: str,
    phone: str,
    scheduleTypeId: num,
    registrationStateId: num,
    eventTours: PropTypes.arrayOf(eventTourShape),
});

export const eventWrapperShape = PropTypes.shape({
    data: eventShape,
    loading: bool,
    // API error shape varies across endpoints; treat as opaque
    // eslint-disable-next-line react/forbid-prop-types
    error: PropTypes.any,
});

export const drawTagTypeShape = PropTypes.shape({
    id: num,
    label: str,
    value: str,
    description: str,
});

const drawRoundMatchRefShape = PropTypes.shape({
    id: numOrStr,
    drawId: numOrStr,
    position: num,
    roundNumber: num,
});

const drawRoundShape = PropTypes.shape({
    id: num,
    number: num,
    roundNumber: num,
    matches: PropTypes.arrayOf(drawRoundMatchRefShape),
});

const drawPlayerShape = PropTypes.shape({
    id: num,
    memberId: num,
    position: num,
});

const drawTeamShape = PropTypes.shape({
    id: num,
    memberIds: PropTypes.arrayOf(num),
});

const drawPartnerShape = PropTypes.shape({
    id: num,
    name: str,
});

export const drawShape = PropTypes.shape({
    id: num,
    drawId: num,
    customDrawId: num,
    name: str,
    parentDrawName: str,
    parentDrawId: num,
    eliminationTypeId: num,
    formatTypeId: num,
    categoryTypeId: num,
    scoringTypeId: num,
    ballTypeId: num,
    eliminationType: PropTypes.oneOfType([str, labelValueShape]),
    drawCategoryType: PropTypes.oneOfType([str, labelValueShape]),
    rounds: PropTypes.arrayOf(drawRoundShape),
    childDraws: PropTypes.arrayOf(PropTypes.shape({ id: num, name: str, categoryTypeId: num })),
    players: PropTypes.arrayOf(drawPlayerShape),
    teams: PropTypes.arrayOf(drawTeamShape),
    published: bool,
    selected: bool,
    isSelected: bool,
    disabled: bool,
    startDateTimeUtc: str,
    totalMatchCount: num,
    scheduledMatchCount: num,
    feedOutRound: num,
    hasPlayoff: bool,
    isTimeBased: bool,
    status: labelValueShape,
    partner: drawPartnerShape,
});

const matchResultPlayersShape = PropTypes.shape({
    winner1: PropTypes.shape({ memberId: num, firstName: str, lastName: str }),
    winner2: PropTypes.shape({ memberId: num, firstName: str, lastName: str }),
    loser1: PropTypes.shape({ memberId: num, firstName: str, lastName: str }),
    loser2: PropTypes.shape({ memberId: num, firstName: str, lastName: str }),
});

const matchSetScoreShape = PropTypes.shape({
    winner: num,
    loser: num,
    tiebreak: num,
    winnerTiebreak: num,
});

const matchResultShape = PropTypes.shape({
    outcome: str,
    completionType: str,
    score: PropTypes.objectOf(matchSetScoreShape),
    players: matchResultPlayersShape,
});

const matchPositionShape = PropTypes.shape({
    typeId: num,
    memberIds: PropTypes.arrayOf(num),
});

const matchParticipatingMemberShape = PropTypes.shape({
    id: num,
    memberId: num,
    firstName: str,
    lastName: str,
    highSchool: str,
    singlesUtr: numOrStr,
    doublesUtr: num,
    utrRange: utrRangeShape,
});

const matchSetShape = PropTypes.shape({
    setNumber: num,
    position1Score: num,
    position2Score: num,
});

export const matchShape = PropTypes.shape({
    id: numOrStr,
    matchId: num,
    drawId: numOrStr,
    position: num,
    pos: num,
    matchPosition: num,
    position1: matchPositionShape,
    position2: matchPositionShape,
    result: matchResultShape,
    date: str,
    startTime: str,
    notBefore: str,
    schedulerStatus: numOrStr,
    drawMatchStatus: numOrStr,
    courtId: num,
    participatingMembers: PropTypes.arrayOf(matchParticipatingMemberShape),
    liveMatch: PropTypes.shape({
        status: str,
        gameState: str,
        startTime: str,
        matchDurationSeconds: num,
        periodScores: PropTypes.arrayOf(PropTypes.shape({
            home_score: numOrStr,
            away_score: numOrStr,
            homeScore: numOrStr,
            awayScore: numOrStr,
            home_tiebreak_score: numOrStr,
            away_tiebreak_score: numOrStr,
            tiebreak_score: numOrStr,
        })),
    }),
    includesTime: bool,
    unsaved: bool,
    scoringTypeId: num,
    drawFormatTypeId: num,
    drawColor: str,
    drawName: str,
    timeLabel: str,
    parentDrawName: str,
    sets: PropTypes.arrayOf(matchSetShape),
    roundNumber: num,
});

const divisionAcceptanceItemShape = PropTypes.shape({
    memberId: num,
    position: num,
    statusId: num,
});

const divisionTournamentShape = PropTypes.shape({
    id: num,
    name: str,
});

const divisionPlayerShape = PropTypes.shape({
    id: num,
    memberId: num,
    firstName: str,
    lastName: str,
});

const divisionCategoryShape = PropTypes.shape({
    id: num,
    value: str,
});

export const divisionShape = PropTypes.shape({
    id: num,
    name: str,
    genderId: num,
    gender: labelValueShape,
    divisionFormatTypeId: num,
    divisionFormatType: labelValueShape,
    divisionCategory: divisionCategoryShape,
    acceptance: PropTypes.oneOfType([
        PropTypes.arrayOf(divisionAcceptanceItemShape),
        PropTypes.shape({
            players: PropTypes.arrayOf(divisionAcceptanceItemShape),
            alternates: PropTypes.arrayOf(divisionAcceptanceItemShape),
        }),
    ]),
    tournament: divisionTournamentShape,
    player: divisionPlayerShape,
    ballType: labelValueShape,
    ballTypeId: num,
    minUtr: num,
    maxUtr: num,
    minAge: num,
    maxAge: num,
    maxPlayers: num,
    maxTeams: num,
    registeredCount: num,
    entryFee: num,
    price: num,
    specificDivisionDateUtc: str,
    teamPaymentTypeId: num,
    registerAsIndividual: bool,
    nonAcceptanceMainCount: num,
});

const clubMemberShape = PropTypes.shape({
    id: num,
    memberId: num,
    firstName: str,
    lastName: str,
    roleId: num,
});

const clubPowerShape = PropTypes.oneOfType([
    num,
    PropTypes.shape({
        tier: num,
        rank: num,
        score: num,
    }),
]);

export const clubShape = PropTypes.shape({
    id: num,
    clubId: num,
    createdClubId: num,
    name: str,
    clubDetails: PropTypes.shape({ id: num, name: str }),
    tierTypeId: num,
    tierType: labelValueShape,
    clubTierType: labelValueShape,
    power: clubPowerShape,
    currentMember: clubMemberShape,
    source: PropTypes.shape({ id: num }),
    roleId: num,
    isLoading: bool,
    isClubCreated: bool,
    isClubUpdated: bool,
    clubDetailsFetched: bool,
    // populated by API on create/update failure; envelope shape varies
    // eslint-disable-next-line react/forbid-prop-types
    createError: PropTypes.any,
    membersInvited: PropTypes.oneOfType([
        num,
        PropTypes.arrayOf(PropTypes.shape({ email: str, status: str })),
    ]),
    stripeAccountId: str,
    rows: PropTypes.arrayOf(PropTypes.shape({ id: num })),
});

export const menuOptionShape = PropTypes.shape({
    value: numOrStr.isRequired,
    label: str,
    id: str,
    show: bool,
    playing: bool,
    clickCategory: str,
    clickContext: str,
    clickLocation: str,
    divisionName: str,
    onClick: fn,
});

const sponsorShape = PropTypes.shape({
    id: num,
    name: str,
    imageUrl: str,
});

export const tierShape = PropTypes.shape({
    displayOrder: num,
    shortName: str,
    sponsors: PropTypes.arrayOf(sponsorShape),
});

const wizardFormShape = PropTypes.shape({
    values: PropTypes.objectOf(numOrStr),
    errors: PropTypes.objectOf(str),
});

export const wizardStepShape = PropTypes.shape({
    id: numOrStr,
    number: num,
    title: str,
    label: str,
    description: str,
    type: str,
    divisionId: num,
    selectedPlayer: PropTypes.shape({
        id: num,
        memberId: num,
        firstName: str,
        lastName: str,
    }),
    invite: PropTypes.oneOfType([str, bool]),
    inviteName: str,
    inviteEmail: str,
    errors: PropTypes.oneOfType([
        PropTypes.arrayOf(str),
        PropTypes.objectOf(str),
    ]),
    tos: bool,
    paid: bool,
    social: bool,
    continue: bool,
    changePartnerFlow: bool,
    showSearchForm: bool,
    showSearchButton: bool,
    playerIsInNeedPartnerList: bool,
    form: wizardFormShape,
    email: str,
});

export const playerBucketShape = PropTypes.shape({
    players: PropTypes.arrayOf(playerShape),
    type: str,
    countSpecialExempt: num,
    visiblePlayersCount: num,
});
